
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/Components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/Components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/Components/ui/accordion.jsx";
import { Button } from "@/Components/ui/button";
import { FileSpreadsheet, Info, ExternalLink, Lightbulb } from "lucide-react";
import { Link } from "react-router-dom";
import ExcelImport from "@/Components/partnerships/ExcelImport";

export default function Import() {
  const handleImportSuccess = (result) => {
    // Modal já mostra o resultado
  };

  return (
    <div className="p-4 sm:p-6 bg-slate-50 min-h-screen">
      <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2 sm:gap-3">
              <FileSpreadsheet className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
              Importar Dados
            </h1>
            <p className="text-slate-600 mt-1">
              Importe parcerias em lote através de arquivos Excel
            </p>
          </div>
        </div>
        {/* Accordion É Importante Saber */}
        <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-0">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="important-info" className="border-none">
                <AccordionTrigger className="hover:no-underline px-3 py-3 sm:px-4 sm:py-4">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0" />
                    <h3 className="font-bold text-amber-900 text-base sm:text-lg break-words">É Importante Saber</h3>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3 sm:px-4 sm:pb-4">
                  <Accordion type="single" collapsible className="w-full space-y-3">
                    
                    {/* Sub-Accordion 1: Tipo de Parceria e Origem */}
                    <AccordionItem value="sub-item-1" className="bg-white rounded-lg border border-amber-200">
                      <AccordionTrigger className="hover:no-underline px-3 py-2 sm:px-4 sm:py-3">
                        <div className="flex items-center gap-2 text-left">
                          <FileSpreadsheet className="w-4 h-4 text-amber-600 flex-shrink-0" />
                          <span className="font-semibold text-amber-900 text-sm break-words">Tipo de Parceria e Origem</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-3 pb-3 sm:px-4 sm:pb-4">
                        <div className="space-y-2 sm:space-y-3">
                          <p className="text-sm text-amber-700">Campos preenchidos após a importação</p>
                          
                          <div className="bg-amber-50 p-3 rounded border border-amber-100">
                            <p className="text-xs font-semibold text-amber-800 mb-2">📋 Como funciona:</p>
                            <ul className="text-xs text-amber-700 space-y-1.5 list-disc list-inside">
                              <li>Estes campos <strong>NÃO</strong> devem ser preenchidos no Excel</li>
                              <li>O sistema define valores padrão automaticamente na importação</li>
                              <li>Você deve editar manualmente após a importação</li>
                            </ul>
                          </div>

                          <div className="bg-blue-50 p-3 rounded border border-blue-100">
                            <p className="text-xs font-semibold text-blue-800 mb-2">✏️ Como editar:</p>
                            <ul className="text-xs text-blue-700 space-y-1.5 list-disc list-inside">
                              <li>Acesse a página de <strong>Parcerias</strong></li>
                              <li>Clique em "Editar" na parceria desejada</li>
                              <li>Selecione o <strong>Tipo de Parceria</strong> correto</li>
                              <li>Selecione a <strong>Origem</strong> correta</li>
                            </ul>
                          </div>

                          <Link to="/partnerships">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-full border-amber-300 text-amber-700 hover:bg-amber-100"
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Ir para Parcerias
                            </Button>
                          </Link>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Sub-Accordion 2: Prazo de Prestação de Contas */}
                    <AccordionItem value="sub-item-2" className="bg-white rounded-lg border border-blue-200">
                      <AccordionTrigger className="hover:no-underline px-3 py-2 sm:px-4 sm:py-3">
                        <div className="flex items-center gap-2 text-left">
                          <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <span className="font-semibold text-blue-900 text-sm break-words">Prazo de Prestação de Contas</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-3 pb-3 sm:px-4 sm:pb-4">
                        <div className="space-y-3 sm:space-y-4">
                          <p className="text-sm text-blue-700 font-medium">O sistema aplica um prazo padrão fixo de 90 dias</p>
                          
                          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                            <p className="text-sm font-semibold text-green-800 mb-3">✅ Quando há Data Início e Data Fim:</p>
                            <ul className="text-sm text-green-700 space-y-2 list-disc list-inside">
                              <li>O campo <strong>Prazo</strong> recebe automaticamente <strong>90 dias</strong></li>
                              <li>Este é um valor <strong>FIXO</strong>, não é calculado pela diferença entre datas</li>
                              <li>A <strong>Data Final Calculada</strong> = Data Fim + 90 dias</li>
                              <li>Esta data final é usada para determinar se está em débito</li>
                            </ul>
                          </div>

                          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                            <p className="text-sm font-semibold text-amber-800 mb-3">⚠️ Quando NÃO há datas:</p>
                            <ul className="text-sm text-amber-700 space-y-2 list-disc list-inside">
                              <li>O campo Prazo fica como <strong>"Não Calculado"</strong></li>
                              <li>A Data Final Calculada fica <strong>vazia</strong></li>
                              <li>O status será <strong>"Verificar"</strong> (requer análise manual)</li>
                            </ul>
                          </div>

                          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <p className="text-sm font-semibold text-blue-800 mb-3">💡 Exemplo prático:</p>
                            <div className="text-sm text-blue-700 space-y-1.5 ml-2">
                              <p><strong>Data Fim:</strong> 31/12/2024</p>
                              <p><strong>Prazo:</strong> 90 dias (padrão)</p>
                              <p><strong>Data Final Calculada:</strong> 31/03/2025</p>
                              <p className="mt-2 text-xs italic">A OSC tem até 31/03/2025 para prestar contas</p>
                            </div>
                          </div>

                          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                            <p className="text-sm font-semibold text-purple-800 mb-3">✏️ Alterando o prazo padrão:</p>
                            <ul className="text-sm text-purple-700 space-y-2 list-disc list-inside">
                              <li>O prazo de <strong>90 dias</strong> é apenas o valor padrão inicial</li>
                              <li>Você pode <strong>alterar</strong> para outros prazos (ex: 30, 40, 60 dias)</li>
                              <li>Para prazos <strong>maiores que 90 dias</strong>, alinhar com a Controlador(a) Geral via e-mail</li>
                              <li>Acesse a página de <strong>Parcerias</strong> e edite a parceria</li>
                              <li>A Data Final Calculada será recalculada automaticamente</li>
                            </ul>
                          </div>

                          <Link to="/partnerships">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="w-full border-purple-300 text-purple-700 hover:bg-purple-100"
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Ir para Parcerias para Editar Prazos
                            </Button>
                          </Link>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                  </Accordion>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Componente de Importação Excel */}
        <ExcelImport onImportSuccess={handleImportSuccess} />
      </div>
    </div>
  );
}
